import   React            ,
       { useState         ,
	     useRef           ,
		 useEffect        ,
		 useLayoutEffect  ,
		 useCallback      }  from 'react';
		 
import   classNames     from 'classnames';

import   styles         from './CollapsiblePanel.module.scss';

import   Icon           from '../../widget/images/Icon';

export interface CollapsiblePanelProps {
  title          : string;
  width?         : string;
  children?      : React.ReactNode;
  onResize?      : () => void;
  onDelete?      : () => void;
  open?          : boolean;
  overlayOnOpen? : boolean;
  maxHeight?     : string;
}

export const CollapsiblePanel = ({ title               ,
                                   width = '100%'       ,
                                   children             ,
                                   onResize             ,
								   onDelete             ,
                                   open                 ,
                                   overlayOnOpen = false ,
                                   maxHeight             }: CollapsiblePanelProps) => {


  const [isCollapsed, setIsCollapsed] = useState(!open ?? true);

  const rootRef     = useRef<HTMLDivElement>(null);
  const contentRef  = useRef<HTMLDivElement>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  // En overlay, le panel déplié se positionne en `position: absolute` par
  // rapport à `root` — mais c'est le PARENT de root (le cadre bordé/teinté
  // ordinaire, ex. .collapsiblePanelWrapper) qui porte la largeur visible
  // du panel fermé. Sans ce calage, le panel déplié ressort plus étroit
  // (inset par le padding de ce parent, jamais connu de ce composant).
  const [overlayRect, setOverlayRect] = useState<{ left: number; width: number } | null>(null);

  const validatedWidth = width.endsWith('%') || width.endsWith('px') ? width : '100%';

  const handleClick = useCallback(() => setIsCollapsed((prev) => !prev), []);

  const adjustHeight = useCallback(() => {

    if (contentRef.current) 
	 {
      if (isCollapsed) 
	        {
             contentRef.current.style.height = '0px';
            } 
	   else {
             //contentRef.current.style.height = `${contentRef.current.scrollHeight}px`;
             contentRef.current.style.height = 'fit-content';
            }
			
      if (onResize) onResize();
     }
  }, [isCollapsed, onResize]);

  useEffect(() => {
    setIsCollapsed(!open);
  }, [open]);

  // Mesurée juste avant que le bloc ne s'ouvre en surimpression — jamais en
  // continu (pas d'écoute de resize) : la largeur du cadre ambiant ne bouge
  // pas pendant que le panel reste ouvert.
  useLayoutEffect(() => {
    if (!overlayOnOpen || isCollapsed) return;
    const root   = rootRef.current;
    const parent = root?.parentElement;
    if (!root || !parent) return;
    const rootBox   = root.getBoundingClientRect();
    const parentBox = parent.getBoundingClientRect();
    setOverlayRect({ left: parentBox.left - rootBox.left, width: parentBox.width });
  }, [overlayOnOpen, isCollapsed]);

  useEffect(() => {
    adjustHeight();
  }, [isCollapsed, adjustHeight]);

  useEffect(() => {

    if (contentRef.current) 
	 {
      observerRef.current = new MutationObserver(() => 
	   {
        adjustHeight(); 
       });

      observerRef.current.observe (contentRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
     }

    return () => {
      if (observerRef.current) 
	   {
        observerRef.current.disconnect();
        observerRef.current = null;
       }
    };
	
  }, [adjustHeight]);


return (
    <div ref       = {rootRef}
         className = {classNames(styles.root)}
	     style     = {{ width: validatedWidth }} >
      
	  <div className={classNames(styles.block_title)} >
        <div className = {classNames(styles.title)}
		     onClick   = {handleClick}>
		  {title}
		</div>
        <div className={classNames(styles.icon_container)}>
            <Icon name               = "triangle"
                  color              = "var(--color-Icon-FG)"
                  hoverColor         = "var(--color-Icon-hover-FG)"
                  pressedState       = {!isCollapsed}
                  pressedTransform   = "rotate(180deg)"
                  unpressedTransform = "rotate(0deg)"
                  width              = {16}
                  height             = {16}
                  onClick            = {handleClick} />
          {
		   onDelete && 
		   (
            <Icon name               = "trashCan"
                  color              = "var(--color-Icon-FG)"
                  hoverColor         = "var(--color-Icon-hover-FG)"
                  width              = {18}
                  height             = {18}
                  onClick            = {onDelete} />
           )
		  }				
        </div>
      </div>
		
      <div ref       = {contentRef}
           className = {classNames(styles.collapsibleContent, { [styles.expanded]: !isCollapsed, [styles.overlay]: overlayOnOpen, [styles.scrollable]: !!maxHeight, })}
           style     = {{
                          ...(maxHeight ? { maxHeight } : {}),
                          ...(overlayOnOpen && overlayRect ? { left: overlayRect.left, width: overlayRect.width } : {}),
                        }} >
        {children || <p>Aucun contenu à afficher</p>}
      </div>
	  
    </div>
  );

};


export default CollapsiblePanel;
